import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import moment from "moment";
import { IMqttMessage, MqttConnectionState, MqttService } from "ngx-mqtt";
import { Observable, Subscription } from "rxjs";
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { ClientSettings } from "src/model/class/client-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { Settings } from "src/model/class/settings";
import { BIGGuestData } from "src/model/class/telegram classes/big-guest-data";
import { BIGTransit } from "src/model/class/telegram classes/big-transit";
import { AccessResult } from "src/components/shared-components/enum/enumAccessControl";
import { CardKind } from "src/components/shared-components/enum/enumCard";
import {
  BIGDeviceRelaysQ896S,
  BIGModeReading
} from "src/components/shared-components/enum/enumDevice";
import {
  AccessControlGatewaySubgroup,
  GatewayKind,
} from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ManufacturerSystem } from "src/components/shared-components/enum/enumMisc";
import { DeviceCardList } from "src/model/interface/device-card-list";
import { GenericDeviceCardListStore } from "src/model/interface/generic-device-card-list-store";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { HistoryTransit } from "src/model/interface/history-transit";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { MQTTMessageAddress } from "src/model/interface/mqtt-message-address";
import {
  deviceCardHandleNewGuestDataTelegramAction,
  deviceCardHandleNewServerConfirmationAddressAction,
  deviceCardStartPopulatateValidListAction,
} from "src/state/actions/device-card.actions";
import { historyTransitAddNewTransitAction } from "src/state/actions/history-transit.actions";
import {
  currentUserData,
  isUserAuthenticated,
} from "src/state/selectors/authentication.selectors";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { listDeviceCard } from "src/state/selectors/device-card.selector";
import { listGateway } from "src/state/selectors/gateway.selectors";
import { currentHistoryTelegramToSend } from "src/state/selectors/history-transit.selector";
import { serverSettings } from "src/state/selectors/server-settings-selector";
import { AddressService } from "./address.service";
import { AuthenticationService } from "./authentication.service";
import { BIGService } from "./big.service";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { PlantCodeService } from "./plant-code.service";
import { ThemeService } from "./theme.service";
import { TimeslotService } from "./timeslot.service";
import { UtilityService } from "./utility.service";
import { CustomerStaff } from "src/model/class/general classes/customer-staff";
import { accessControlledResult } from "src/model/interface/access-Controlled-Result";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

/**
 * Execute code validation for stand alone device.
 * handle handshake memory
 */
@Injectable({
  providedIn: "root",
})
export class ValidListHandlerService implements Partial<iMqttHandling>, iEvent {
  private _logContext: string = "Valid-list-handler.service";

  private _validDeviceCardListSubscription: Subscription = null;
  private _clientSettingSubscription: Subscription = null;
  private _listGatewaySubscription: Subscription = null;
  private _selectCurrentUserSubscription: Subscription = null;
  private _lastTranistToDigestSubscription: Subscription = null;
  private _serverSettingSubscription: Subscription = null;
  private _serverSetting: Settings = null;
  private c: Subscription = null;

  private _listDeviceCard: Array<DeviceCardList> = new Array<DeviceCardList>();
  private _listCardOwner: Array<CustomerStaff> = new Array<CustomerStaff>();
  private _clientSettings: ClientSettings = null;
  private _listGuestDataAddresses: Array<string> = null;
  private _listServerConfirmationAddresses: Array<string> = null;
  private _accessControlGateway: Gateway = null;
  private _secondsResendTransit: number = 15;
  private _intervalResendTimer: any = null;

  public _mqttAddressSubscriptionsMap: any = null;

  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _store: Store,
    private _addressService: AddressService,
    private _loggerService: LoggerService,
    private _mqttClientService: MqttClientService,
    private _authService: AuthenticationService,
    private _bigService: BIGService,
    private _utilityService: UtilityService,
    private _eventService: EventService,
    private _timeSlotService: TimeslotService,
    private mqttNativeService: MqttService,
    private _plantCodeService: PlantCodeService,
    private _themeService: ThemeService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
    this.loadAccessControlGateway();
    this.loadDeviceCardValidList();
    this.loadCurrentClientSettings();
    this.loadTransitToDigest();

    this.loadCurrentUser();
    this.loadServerSettings();
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeStoreSelect();
  }

  /**
   * Initialize service
   */
  public init(): void {
    this._store.dispatch(deviceCardStartPopulatateValidListAction());
  }

  /**
   * Force reload data service
   */
  public forceReloadData(): void {
    this.init();
  }

  /**
   * Load access control address by avaiable id card list
   *
   * @param listCardDevice
   */
  public async loadAccessControlAddressesAndSubscribe(
    listIDDevices
  ): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "loadAccessControlAddressesAndSubscribe()",
      "START"
    );

    // this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this._listGuestDataAddresses = this.getAccessControlAddressesByProperty(
      listIDDevices,
      AccessControlGatewaySubgroup.GUEST_DATA
    );

    this._listServerConfirmationAddresses =
      this.getAccessControlAddressesByProperty(
        listIDDevices,
        AccessControlGatewaySubgroup.SERVER_CONFIRMATION
      );

    this.subscribeGuestDataAddresses();
    this.subscribeServerConfirmationAddressess();
  }

  /**
   * Return card code (card number) by input UID.
   *
   * @param uid Input code
   * @returns code
   */
  public getCodeByUID(uid: string, idDevice: number) {
    let returnedCode = "000000";

    let foundedCard = this._listDeviceCard.find(
      (card: DeviceCardList) => card.uid === uid && card.idDevice === idDevice
    );

    if (foundedCard) {
      returnedCode = foundedCard.code;
    }
    return returnedCode;
  }

  /**
   * Return access result by input code. ALSO, if info about the cardOwner of the given code is locally present, this method return it too.
   * 
   * It execute code validation based on current time and
   *
   * @param idDevice ID Device to check card
   * @param code Input code
   * @returns
   */
  public getAccessResultByInputCode(
    idDevice: number,
    code: string,
    plantCode?: number,
    cardKind?: CardKind,
    manufacturerSystem?: ManufacturerSystem,
    readingMode?: BIGModeReading
  ): accessControlledResult {


    let accessResult: AccessResult = AccessResult.ACCESS_DENIED;
    let cardOwner: CustomerStaff = null;
    let foundedCard: DeviceCardList;

    if (cardKind == CardKind.REAL_CARD && readingMode == BIGModeReading.UID) {
      foundedCard = this._listDeviceCard.find(
        (card: DeviceCardList) =>
          card.uid === code && card.idDevice === idDevice
      );
    } else {


      foundedCard = this._listDeviceCard.find((card: DeviceCardList) => {

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "getAccessResultByInputCode()",
          "Virtual-access. valid cards code as NUmber: " + parseInt(card.code)
        );

        return parseInt(card.code) === parseInt(code) && card.idDevice === idDevice;
      });
    }

    const now = moment();

    if (foundedCard) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "getAccessResultByInputCode()",
        "Virtual-access. CARD FOUND!"
      );


      cardOwner = this._listCardOwner.find(
        (owner: CustomerStaff) =>
          owner.id === foundedCard.cardOwnerID
      );

      const isCardExpired: boolean = this.checkIfCardIsExpired(
        foundedCard.expirationDate
      );
      let isDateTimeinTimeS: boolean =
        this._timeSlotService.isDateTimeInTimeSlot(foundedCard.idTimeSlot);

      // if(this._currentPlant.operativeMode === BIGDeviceOperationMode.STANDARD;){

      let isPlantCodeCorrect: boolean = this.verifyPlantCodeStandAlone(
        plantCode,
        cardKind,
        manufacturerSystem,
        readingMode
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "getAccessResultByInputCode()",
        "Virtual-access. plantCode from card: " + plantCode + " | isPlantCodeCorrect: " + isPlantCodeCorrect
      );

      if (!isCardExpired && isDateTimeinTimeS && isPlantCodeCorrect) {
        accessResult = AccessResult.ACCESS_ALLOWED;
      } else if (!isDateTimeinTimeS) {
        accessResult = AccessResult.ACCESS_DENIED_WRONG_TIMESLOT;
      } else {
        if (!isPlantCodeCorrect) {
          accessResult = AccessResult.ACCESS_DENIED_WRONG_PLANT_CODE;
        } else {
          accessResult = AccessResult.ACCESS_DENIED_EXPIRED_CARD;
        }
      }
    }

    return {
      accessResult: accessResult,
      cardOwner: cardOwner
    }

  }

  /**
   * Check if the plant code is correct (only for real card AND when touch is in Stand-Alone mode)
   */
  private verifyPlantCodeStandAlone(
    plantCode: number,
    cardKind: CardKind,
    manufacturer?: ManufacturerSystem,
    readingMode?: BIGModeReading
  ): boolean {
    let isPlantCodeCorrect: boolean = true;
    if (cardKind == CardKind.REAL_CARD && readingMode != BIGModeReading.UID) {
      if (
        plantCode != this._plantCodeService.calculatePlantCode(manufacturer, plantCode, -1)
      ) {
        isPlantCodeCorrect = false;
      }
    }
    return isPlantCodeCorrect;
  }//

  /**
   * Check in releated card expiration date is expired
   *
   * @param expirationDate Expiration date
   * @returns
   */
  public checkIfCardIsExpired(expirationDate: string): boolean {
    return moment().isSameOrAfter(
      moment(
        expirationDate,
        MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST
      )
    );
  }

  /**
   * Exceute valid access command when access result is configured
   */
  public executeValidAccessCommand(): void {
    switch (this._clientSettings.defaultRelay) {
      case BIGDeviceRelaysQ896S.RELAY_1:
      case BIGDeviceRelaysQ896S.RELAY_2:
        this._bigService.closeRelay(
          this._clientSettings.defaultRelay,
          0,
          this._clientSettings.defaultRelayActivationTime * 1000
        );
        break;
    }
  } // //

  /**
   * Put access code telegram in handshake memory
   *
   * @param idDevice ID Device releated to virtual access component
   * @param code Code to send access code
   * @param accessResult
   */
  public sendAccessCodeTelegram(
    idDevice: number,
    code: string,
    accessResult: AccessResult,
    uid: string
  ): void {
    const address: string = this._addressService.getVirtualAccessAddress(
      idDevice,
      AccessControlGatewaySubgroup.TRANSIT
    );

    this._store.dispatch(
      historyTransitAddNewTransitAction({
        idDevice,
        instanceGuid: this._accessControlGateway.instanceGUID,
        address,
        code,
        accessResult,
        uid,
      })
    );
  }

  /**
   * Cast guest data object to device card list object
   *
   * @param guestData Guest data object
   * @returns
   */
  public guestDataObjectToDeviceCardListObject(
    idDevice: number,
    guestData: BIGGuestData
  ): DeviceCardList {
    return {
      idDevice,
      code: guestData.code,
      expirationDate: moment(
        guestData.date + " " + guestData.time,
        "DDMMYYYY hhmm"
      ).format(MomentFormats.MOMENT_DATETIME_FORMAT_DEVICE_VALID_LIST),
      idTimeSlot: guestData.idTimeSlot,
      idCalendar: guestData.idCalendar,
      idProfilePayment: guestData.idPaymentProfile,
      uid: guestData?.uid,
      cardOwnerID: -1
    };
  }

  /**
   * Build history transit object type
   *
   * @param idPlant ID Referenced plant
   * @param idDevice ID Referenced plant
   * @param instanceGuid Instance guid to write transit
   * @param address Address to write transit
   * @param code Code insert by user
   * @param accessResult Access result produced by code
   * @param date Date access execution
   * @param time Time access execution
   * @returns
   */
  public buildHistoryTransitObject(
    idPlant: number,
    idDevice: number,
    instanceGuid: string,
    address: string,
    code: string,
    accessResult: AccessResult,
    date: string,
    time: string,
    uid: string
  ): HistoryTransit {
    const uuid = this._utilityService.generateUUID();

    return {
      uuid,
      idPlant,
      idDevice,
      instanceGuid,
      address,
      code,
      accessResult,
      date,
      time,
      uid,
    };
  }

  //#region Event Service
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnlineMqtt.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "OnComeBackOnlineMqtt()",
          "REACT"
        );
        // if (!this._themeService.isDigitalPlace) {
        //   this._store.dispatch(deviceCardStartPopulatateValidListAction());
        // }
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(() => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "OnMakeLogout()",
          "LOGOUT"
        );

        this.unsubscribeToMQTTAddresses();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Get transit object by history object
   *
   * @param historyTelegram History transit object instance
   * @returns
   */
  private buildTransitObjecctByHistoryTelegram(
    historyTelegram: HistoryTransit
  ): BIGTransit {
    const transitObject: BIGTransit = new BIGTransit();
    transitObject.code = historyTelegram.code;
    transitObject.accessResult = historyTelegram.accessResult.toString();
    transitObject.date = historyTelegram.date;
    transitObject.time = historyTelegram.time;
    transitObject.uid = historyTelegram.uid;

    return transitObject;
  }

  /**
   * Subscribe to store to get valid list selector
   */
  private loadDeviceCardValidList(): void {
    this._validDeviceCardListSubscription = this._store
      .select(listDeviceCard)
      .pipe(
        filter(
          (listDeviceCardStore: GenericDeviceCardListStore) =>
            listDeviceCardStore?.loaded
        ),
        filter(
          (listDeviceCardStore: GenericDeviceCardListStore) =>
            !listDeviceCardStore.error
        ),
        tap((listDeviceCardStore: GenericDeviceCardListStore) => {
          if (listDeviceCardStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listDeviceCard)",
              "loadDeviceCardValidList()",
              "We are using old data!!"
            );
          }
        }),
        map(
          (listDeviceCardStore: GenericDeviceCardListStore) => {
            return {
              validList: listDeviceCardStore.validList,
              cardOnwerList: listDeviceCardStore.cardOwnerList || new Array<CustomerStaff>()
            }
          }
        )
      )
      .subscribe((selectedObject: any) => {

        this._listDeviceCard = selectedObject.validList as Array<DeviceCardList>;

        this._listCardOwner = selectedObject.cardOnwerList as Array<CustomerStaff>;

      });
  }

  /**
   * Load current client settings
   */
  private loadCurrentClientSettings(): void {
    this._clientSettingSubscription = this._store
      .select(clientSettings)
      .pipe(filter((clientSettings) => clientSettings !== null))
      .subscribe((clientSettings) => {
        this._clientSettings = clientSettings;
      });
  }

  /**
   * Load access control gateay
   */
  private loadAccessControlGateway(): void {

    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "loadAccessControlGateway()",
      "START"
    );

    this._listGatewaySubscription = this._store
      .select(listGateway)
      .pipe(
        filter(
          (listGatewayStore: GenericStoreApiData) => !!listGatewayStore
        ),
        filter(
          (listGatewayStore: GenericStoreApiData) => listGatewayStore?.loaded
        ),
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            !(
              listGatewayStore.errorGeneric ||
              listGatewayStore.errorNotYetImplemented
            )
        ),
        tap((listGatewayStore: GenericStoreApiData) => {
          if (listGatewayStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "loadAccessControlGateway()",
              "We are using old data!"
            );
          }
        }),

        map(
          (listGatewayStore: GenericStoreApiData) =>
            listGatewayStore.data?.data || []
        ),
        map((listGateway: Array<Gateway>) => {
          return listGateway.find(
            (gateway: Gateway) => gateway.kind === GatewayKind.ACCESS_CONTROL
          );
        })
      )
      .subscribe((accessControlGateway: Gateway) => {
        this._accessControlGateway = accessControlGateway;
      });
  }

  /**
   * Load guest data addresses
   *
   * @param listCardDevice List device card to subscribe
   */
  private getAccessControlAddressesByProperty(
    listIDDevices: Array<number>,
    accessControlProperty: AccessControlGatewaySubgroup
  ): Array<string> {
    return listIDDevices.map((idDevice: number) =>
      this._addressService.getVirtualAccessAddress(
        idDevice,
        accessControlProperty
      )
    );
  }

  /**
   * Subscribbe guest data addresses
   */
  private subscribeGuestDataAddresses(): void {
    this._listGuestDataAddresses.map(async (guestDataAddress: string) => {
      const fullAddress: string =
        this._accessControlGateway.instanceGUID + "_" + guestDataAddress;

      const promsieObservable = await this.createObservableMqttMessage(
        guestDataAddress
      );

      const subscription: Subscription = promsieObservable
        .pipe(
          map((mqttMessageObject: any) => mqttMessageObject.Message),
          tap((mqttMessage: any) => {

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              this._logContext,
              "subscribeGuestDataAddresses",
              "Sending ACK"
            );

            this._mqttClientService.sendACK(
              mqttMessage.Ref,
              this._accessControlGateway.instanceGUID
            );
          }),
          map((mqttMessage: any) => mqttMessage.Value)
        )
        .subscribe((mqttMessageValue: string) =>
          this.handleNewGuestDataTelegram(guestDataAddress, mqttMessageValue)
        );

      await this.checkLocalAddressSubscriptionMap(fullAddress, subscription);
    });
  }

  /**
   * Suubscribe server confirmation addresses
   */
  private subscribeServerConfirmationAddressess(): void {
    this._listServerConfirmationAddresses.map(
      async (serverConfirmationAddress: string) => {
        //await this.checkmqttAddressesMaps("subscribeServerConfirmationAddressess()");

        const fullAddress: string =
          this._accessControlGateway.instanceGUID +
          "_" +
          serverConfirmationAddress;

        const promsieObservable = await this.createObservableMqttMessage(
          serverConfirmationAddress
        );

        const subscription: Subscription = promsieObservable.subscribe(
          (mqttMessage: MQTTMessageAddress) =>
            this.handlNewServerConfirmationTelegram(
              serverConfirmationAddress,
              mqttMessage?.Message?.Value || ""
            )
        );

        await this.checkLocalAddressSubscriptionMap(fullAddress, subscription);

        //await this.checkmqttAddressesMaps("subscribeServerConfirmationAddressess()");
      }
    );
  }

  /**
   * Create promise observable subscription for generic access control address
   *
   * @param guestDataAddress Address to subscribe
   * @returns
   */
  public async createObservableMqttMessage(
    genericAccessControlAddress: string
  ): Promise<Observable<any>> {
    const promiseSubscriptionGuestAddress: Observable<IMqttMessage> =
      await this._mqttClientService.subscribeTopic(
        this._accessControlGateway.instanceGUID +
        "_" +
        genericAccessControlAddress
      );

    return promiseSubscriptionGuestAddress.pipe(
      map((message: IMqttMessage) => JSON.parse(message.payload.toString()))
    );
  }

  public async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }

    return result;
  }

  /**
   * Unsubscribe to mqtt address
   */
  public async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }
  //#endregion

  /**
   * Handle unsubscription from store
   */
  private unsubscribeStoreSelect(): void {
    this._validDeviceCardListSubscription?.unsubscribe();
    this._clientSettingSubscription?.unsubscribe();
    this._listGatewaySubscription?.unsubscribe();
    this._selectCurrentUserSubscription?.unsubscribe();
    this._lastTranistToDigestSubscription?.unsubscribe();
    this._serverSettingSubscription?.unsubscribe();
  }

  /**
   * Load current user from store
   */
  private loadCurrentUser(): void {
    this._selectCurrentUserSubscription = this._store
      .select(currentUserData)
      .pipe(
        distinctUntilChanged(
          (
            previousSession: GenericUserData,
            currentSession: GenericUserData
          ) => {
            let accessTokenPreviosSession: string =
              previousSession?.currentUser?.accessToken || "";
            let accessTokenCurrentSession: string =
              currentSession?.currentUser?.accessToken || "";

            return accessTokenPreviosSession === accessTokenCurrentSession;
          }
        ),
        map((currentUserData: GenericUserData) => currentUserData?.currentUser),
        tap((currentUser: CurrentSessionData) => {
          if (
            currentUser &&
            this._authService.isAccessTokenValid(currentUser) &&
            this._authService.isRefreshTokenValid(currentUser)
          ) {

            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(currentUser)",
              "loadCurrentUser()",
              "REACT"
            );

            this.init();
            this._timeSlotService.Init();
          }
        })
      )
      .subscribe((currentUser: CurrentSessionData) => { });
  }

  /**
   * Handle new guest data addresss.
   * It dispatches store action to handle its telegram
   *
   * @param guestDataAddress Guest data address
   * @param telegramValue Telegram content value
   */
  private handleNewGuestDataTelegram(
    guestDataAddress: string,
    telegramValue: string
  ): void {
    const idDevice: number = Number(
      this._addressService.getMiddleGroupAddressFromAddress(guestDataAddress) ||
      0
    );

    if (idDevice > 0) {
      this._store.dispatch(
        deviceCardHandleNewGuestDataTelegramAction({
          idDevice,
          guestDataTelegram: telegramValue,
        })
      );
    }
  }

  /**
   * Handle server confirmation address
   * It dispatches store action to handle its telegram
   *
   * @param serverConfirmationAddress Server confirmation address
   * @param telegramValue Telegram value
   */
  private handlNewServerConfirmationTelegram(
    serverConfirmationAddress: string,
    telegramValue: string
  ): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handlNewServerConfirmationTelegram()",
      "HANDLING FROM : " +
      serverConfirmationAddress +
      " | value: " +
      telegramValue
    );

    const idDevice: number = Number(
      this._addressService.getMiddleGroupAddressFromAddress(
        serverConfirmationAddress
      ) || 0
    );

    if (idDevice > 0) {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handlNewServerConfirmationTelegram()",
        "Dispatching action for server confirmation from: " + idDevice
      );

      this._store.dispatch(
        deviceCardHandleNewServerConfirmationAddressAction({
          idDevice,
          serverConfirmationTelegram: telegramValue,
        })
      );
    }
  }

  /**
   * Select current history telegram to digest from list avaiable transit
   */
  private loadTransitToDigest(): void {
    this._lastTranistToDigestSubscription = this._store
      .select(currentHistoryTelegramToSend)
      .pipe(
        distinctUntilChanged(
          (
            previousHistoryTransit: HistoryTransit,
            currentHistoryTransit: HistoryTransit
          ) => {
            return previousHistoryTransit?.uuid === currentHistoryTransit?.uuid;
          }
        ),
        tap((historyTelegram: HistoryTransit) => this.stopTransitDigesting()),
        filter((historyTelegram: HistoryTransit) => historyTelegram !== null),
        delay(1000)
      )
      .subscribe((historyTelegram: HistoryTransit) => {
        this.startTransitDigesting(historyTelegram);
      });
  }

  /**
   * Start transit telegram digesting.
   * It also start timer (who evalutate mqtt connection state) to resend telegram
   * until input history transit is not changed
   *
   * @param historyTelegram History transit to send
   */
  private startTransitDigesting(historyTelegram: HistoryTransit): void {
    this.sendTransitTelegram(historyTelegram);

    this._intervalResendTimer = setInterval(() => {
      this.sendTransitTelegram(historyTelegram);
    }, this._secondsResendTransit * 1000);
  }

  /**
   * Stop interval telegram digesting
   */
  private stopTransitDigesting(): void {
    if (this._intervalResendTimer) {
      clearInterval(this._intervalResendTimer);
    }
  }

  /**
   * Send single telegram object
   *
   * @param historyTelegram History telegram entry
   */
  private sendTransitTelegram(historyTelegram: HistoryTransit): void {
    try {
      if (this._mqttClientService.Status === MqttConnectionState.CONNECTED) {
        const fullTransitAddress =
          historyTelegram.instanceGuid + "_" + historyTelegram.address;
        const transitTelegram: BIGTransit =
          this.buildTransitObjecctByHistoryTelegram(historyTelegram);

        transitTelegram.buildTelegramByProps();

        this._mqttClientService.publishAddress(
          fullTransitAddress,
          transitTelegram.telegram
        );
      }
    } catch (exception) { }
  }

  /**
   * Load server settings
   */
  public loadServerSettings(): void {
    this._serverSettingSubscription = this._store
      .select(serverSettings)
      .subscribe((settings: Settings) => {
        this._serverSetting = settings;
      });
  }
}
